import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext.js';
import { createReservation, updateReservation, checkOverlap } from '../../api/resourceMngApi.js';
import CommonDatePicker from "../../components/DatePicker/CommonDatePicker.js";
import "react-datepicker/dist/react-datepicker.css";
import './Reservation.css';

import ResourcePicker from './ResourcePicker.js';
import ResourceDetailModal from './ResourceDetailModal.js';

// ✅ 안전한 날짜/시간 변환 함수
const parseDateTime = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const safeTime = timeStr.length === 5 ? `${timeStr}:00` : timeStr; // HH:mm → HH:mm:00
  const isoString = `${dateStr}T${safeTime}`;
  const parsed = new Date(isoString);
  return isNaN(parsed.getTime()) ? null : parsed;
};

function ReservationCreate({ reservation, defaultDate, defaultEndDate, actionType, onClose, onRefresh }) {
  const isEdit = actionType === 'edit';
  const now = new Date();

  // ✅ 상태 초기화
  const [subject, setSubject] = useState(isEdit ? reservation.subject : '');

  // 10분 단위로 올림(rounding)
  const roundToInterval = (date, interval = 10) => {
    const ms = 1000 * 60 * interval;
    return new Date(Math.ceil(date.getTime() / ms) * ms);
  };

  const [startDate, setStartDate] = useState(
    isEdit
      ? parseDateTime(reservation.resStartDate, reservation.resStartTime) // 수정 모드 → 기존 예약 시작일시
      : (defaultDate
        ? roundToInterval(new Date(defaultDate), 10)
        : roundToInterval(now, 10)) // 신규 모드 → 현재시간을 10분 단위로 보정
  );

  const [endDate, setEndDate] = useState(
    isEdit
      ? parseDateTime(reservation.resEndDate, reservation.resEndTime) // 수정 모드 → 기존 예약 종료일시
      : (defaultEndDate
        ? roundToInterval(new Date(defaultEndDate), 10)
        : roundToInterval(new Date(now.getTime() + 60 * 60 * 1000), 10)) // 신규 모드 → 현재시간+1시간을 10분 단위로 보정
  );
  
  const [resourceId, setResourceId] = useState(isEdit ? reservation.resId : '');
  const [resourceName, setResourceName] = useState(isEdit ? reservation.resName : '');
  const [resourceColor, setResourceColor] = useState(isEdit ? reservation.resColor : '');
  const [resourceCategory, setResourceCategory] = useState(isEdit ? reservation.resCategory : '');


  // ✅ 승인자 상태
  const [approverId, setApproverId] = useState(isEdit ? reservation.approver_id || reservation.approverId : '');
  const [approverName, setApproverName] = useState(isEdit ? reservation.approver_name || reservation.approverName : '');

  const [attendees, setAttendees] = useState(isEdit ? reservation.memberCount : '');
  const [notes, setNotes] = useState(isEdit ? reservation.comment : '');

  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);

  const navigate = useNavigate();
  const { token, user } = useContext(AuthContext);

  // ✅ reservation 값이 바뀔 때 approver 상태 동기화
  useEffect(() => {
    if (isEdit && reservation) {
      setApproverId(reservation.approver_id || reservation.approverId || '');
      setApproverName(reservation.approver_name || reservation.approverName || '');
    }
  }, [isEdit, reservation]);

  const handleCheckOverlap = async () => {
    if (!resourceId) {
      alert("자원을 먼저 선택하세요.");
      return;
    }

    if (!startDate || !endDate) {
      alert("시작/종료 일시를 선택하세요.");
      return;
    }

    const now = new Date();

    // 시작일시가 현재보다 같거나 이전이면 오류
    if (startDate instanceof Date && startDate <= now) {
      alert("시작일시는 현재 시각보다 늦어야 합니다.");
      return;
    }

    // 종료일시가 시작일시보다 같거나 이전이면 오류
    if (endDate instanceof Date && endDate <= startDate) {
      alert("종료일시는 시작일시보다 늦어야 합니다.");
      return;
    }

    const payload = {
      res_id: resourceId,
      res_st_date: toDateText(startDate),
      res_st_time: toTimeText(startDate),
      res_ed_date: toDateText(endDate),
      res_ed_time: toTimeText(endDate),
      reservationId: isEdit ? reservation.id : null
    };

    try {
      const data = await checkOverlap(payload);

      if (data.overlaps && data.overlaps.length > 0) {
        alert("이미 예약된 시간대입니다.");
      } else {
        alert("예약 가능합니다.");
      }
    } catch (err) {
      console.error(err);
      alert("중복 확인 중 오류가 발생했습니다.");
    }
  };
  
  const handleClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    } else {
      navigate('/reservations/calendar');
    }
  };

  const toDateText = (d) => {
    if (!d) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const toTimeText = (d) => {
    if (!d) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}:00`;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();

    const now = new Date();

    if (!subject || subject.trim() === "") {
      alert("제목을 반드시 입력해야 합니다.");
      return;
    }

    if (!resourceId || !resourceName) {
      alert("자원을 반드시 선택해야 합니다.");
      return;
    }

    if (!startDate || !endDate) {
      alert("시작/종료 일시를 모두 선택하세요.");
      return;
    }

    if (endDate < startDate) {
      alert("종료일시는 시작일시보다 늦어야 합니다.");
      return;
    }

    if (startDate < now) {
      alert("시작일시는 현재 시각보다 늦어야 합니다.");
      return;
    }

    if (!attendees || parseInt(attendees, 10) <= 0) {
      alert("참석인원을 반드시 입력해야 합니다. (1 이상 숫자)");
      return;
    }

    try {
      const reservationData = {
        subject,
        res_id: resourceId,
        res_name: resourceName,
        res_color: resourceColor,
        resCategory: resourceCategory,   // 자원구분 포함
        approver_id: approverId,
        approver_name: approverName,
        writer_id: user?.userid,
        writer_name: user?.name,
        res_st_date: toDateText(startDate),
        res_st_time: toTimeText(startDate),
        res_ed_date: toDateText(endDate),
        res_ed_time: toTimeText(endDate),
        member_cnt: parseInt(attendees, 10),
        comment: notes,
        status: resourceCategory === "회의실"
          ? "승인"
          : (isEdit ? reservation.status : "대기")
        //status: isEdit ? reservation.status : "대기"
      };
      //console.log("reservationData:", JSON.stringify(reservationData, null, 2));
      if (isEdit) {
        await updateReservation(reservation.id, reservationData, token);
      } else {
        await createReservation(reservationData, token);
      }

      if (actionType === "create") {
        navigate("/reservations/calendar?status=pending");
      } else {
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh();
        }
        handleClose();
      }
    } catch (err) {
      if (err.response?.status === 409) {
        alert(err.response.data.message || "이미 예약된 시간대입니다.");
        return;
      }
      alert(isEdit ? "예약 수정 중 오류가 발생했습니다." : "예약 등록 중 오류가 발생했습니다.");
    }
  };

  return (
    <div className="reservation-wrapper">
      <div className="reservation-form">
        <div className="reservation-title">
          {isEdit ? '자원예약 수정' : '자원예약'}
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* 제목 */}
          <div className="mb-3 reservation-form-row">
            <label className="form-label fw-bold">제목</label>
            <input
              type="text"
              className="form-control reservation-input"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              required
            />
          </div>

          {/* 자원구분 / 예약자원 */}
          <div className="mb-3 reservation-form-row">
            <label className="form-label fw-bold">자원구분 / 예약자원</label>
            <div className="d-flex gap-2 align-items-center">
              {/* ✅ 선택색상 넓이만 확대 (높이는 그대로) */}
              <div
                className="color-preview"
                style={{
                  backgroundColor: resourceColor || '#1edc71',
                  width: '80px'   // 기존보다 넓게 지정
                }}
              >
                선택색상
              </div>

              {/* ✅ 자원구분 입력창 */}
              <input
                type="text"
                className="form-control resource-field"
                style={{ width: '20%' }}   // 자원구분은 그대로 20%
                value={resourceCategory || ''}
                readOnly
                placeholder="선택된 자원구분"
              />
              <span className="date-sep"> - </span>
              {/* ✅ 선택된 자원 입력창 (폭 줄임) */}
              <input
                type="text"
                className="form-control resource-field"
                style={{ width: '40%' }}   // 기존보다 줄여서 색상 박스와 균형 맞춤
                value={resourceName}
                readOnly
                placeholder="선택된 자원이 없습니다."
              />
              
              <button
                type="button"
                className="btn btn-outline-secondary btn-find"
                onClick={() => setPickerOpen(true)}
              >
                찾기
              </button>
              <button
                type="button"
                className="btn btn-outline-primary btn-detail"
                onClick={() => setDetailOpen(true)}
                disabled={!resourceId}
              >
                상세조회
              </button>
            </div>
          </div>

          {/* 예약일시 */}
          <div className="mb-3 reservation-form-row">
            <label className="form-label fw-bold">예약일시</label>
            <div className="d-flex align-items-center gap-2">
              <CommonDatePicker
                value={startDate}
                onChange={(date) => {
                  const rounded = roundToInterval(date, 10);
                  if (rounded < new Date()) {
                    alert("현재 시간보다 이전 시간은 선택할 수 없습니다.");
                    return;
                  }
                  setStartDate(rounded);
                }}
                placeholder="시작일시 선택"
                showTime={true}
                minDate={now}
              />
              <span className="date-sep">~</span>
             <CommonDatePicker
                value={endDate}
                onChange={(date) => setEndDate(roundToInterval(date, 10))}
                placeholder="종료일시 선택"
                showTime={true}
                minDate={startDate || now} // ✅ 종료일시는 시작일시 이후만 선택 가능
                minTime={startDate} // ✅ 같은 날이면 시작시간 이후만 선택 가능
              />
              {/* ✅ 종료일시 옆에 버튼 배치 */}
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={handleCheckOverlap}
              >
                중복 확인
              </button>
            </div>
          </div>

          {/* 참석인원 */}
          <div className="mb-3 reservation-form-row">
            <label className="form-label fw-bold">인원</label>
            <input
              type="number"
              min="1"
              className="form-control reservation-input"
              value={attendees}
              onChange={(e) => setAttendees(e.target.value)}
              placeholder="예: 10"
              required
            />
          </div>

          {/* 비고 */}
          <div className="mb-3 reservation-form-row">
            <label className="form-label fw-bold">요청사항</label>
            <textarea
              className="form-control notes-area"
              rows="4"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="추가 요청사항이나 메모를 입력하세요."
            />
          </div>

          {/* 버튼 영역 */}
          <div className="d-flex justify-content-end gap-2">
            <button type="submit" className="btn btn-success">
              {isEdit ? '예약 수정' : (actionType === 'create' ? '예약 등록' : '확인')}
            </button>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={handleClose}
            >
              닫기
            </button>
          </div>
        </form>
      </div>
      
      {/* 자원 찾기 모달 */}
      {pickerOpen && (
        <ResourcePicker
          onSelect={(res) => {
            setResourceId(res.id);
            setResourceName(res.res_name);
            setResourceColor(res.res_color);
            setResourceCategory(res.res_category);   // ✅ 자원구분 값 저장
            setApproverId(res.approver_id);     // ✅ 승인자 ID도 함께 저장
            setApproverName(res.approver_name); // ✅ 승인자 이름도 함께 저장
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}

      {/* 자원 상세조회 모달 */}
      {detailOpen && (
        <ResourceDetailModal
          resourceId={resourceId}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </div>
  );
}

export default ReservationCreate;
